import { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SortDescriptor } from '@progress/kendo-data-query/dist/npm/sort-descriptor'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import {
  IntroducingBrokerDto,
  IntroducingBrokerFilterKey,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getIntroducingBrokerFilterFields } from 'components/Filter/introducingBrokerFields'
import { Spinner } from 'components/Spinner'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import {
  fetchIntroducingBrokerParentTypesAction,
  fetchIntroducingBrokerStatusesAction,
} from 'store/partnerships/partnerships.actions'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { SelectClientEvent, SelectClientTable } from '../../Clients/SelectClientTable'
import introducingBrokerColumn from '../introducingBrokerColumn'
import { normalizeIntroducingBrokersSortQuery } from './normalizer'
import { SelectIBEvent, SelectIBSAssignTable } from './SelectIBSAssignTable'

const useMapStateToProps = () => ({
  introducingBrokerStatus: useAppSelector((state) => state.partnerships.introducingBrokerStatus),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  introducingBrokerParentTypes: useAppSelector(
    (state) => state.partnerships.introducingBrokerParentTypes
  ),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  return {
    fetchIntroducingBrokerStatuses: async () =>
      dispatch(fetchIntroducingBrokerStatusesAction(partnershipsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchIntroducingBrokerParentTypes: async () =>
      dispatch(fetchIntroducingBrokerParentTypesAction(partnershipsApiClient)),
  }
}

const getSelectedIds = (introducingBrokers: IntroducingBrokerDto[]) => {
  return introducingBrokers.filter((x) => x.selected).map((x) => x.id)
}

const IntroducingBrokersOverview = (): JSX.Element => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const crumbs = [{ title: 'Introducing Brokers', link: links.recordsIntroducingBrokers.url }]

  const [introducingBrokers, setIntroducingBrokers] = useState<IntroducingBrokerDto[]>([])

  const [isAssignUsersFirstPageModalOpen, setAssignUsersFirstPageModalOpen] =
    useState<boolean>(false)
  const [isAssignUsersSearchModalOpen, setAssignUsersSearchModalOpen] = useState<boolean>(false)
  const [isAssignUsersConfirmationModalOpen, setAssignUsersConfirmationModalOpen] =
    useState<boolean>(false)
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { introducingBrokerStatus, introducingBrokerParentTypes, gridFilterByFilterIdSelector } =
    useMapStateToProps()
  const { fetchIntroducingBrokerStatuses, fetchIntroducingBrokerParentTypes } =
    useMapDispatchToProps()
  const permissions = auth?.permissions || []

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<IntroducingBrokerDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await partnershipsApiClient.getIntroducingBrokers({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(introducingBrokerStatus)) await fetchIntroducingBrokerStatuses()
          if (isEmpty(introducingBrokerParentTypes)) await fetchIntroducingBrokerParentTypes()

          if (response?.items) {
            setIntroducingBrokers(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<IntroducingBrokerDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeIntroducingBrokersSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await partnershipsApiClient.getIntroducingBrokers({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setIntroducingBrokers(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await partnershipsApiClient.getIntroducingBrokers({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setIntroducingBrokers(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await partnershipsApiClient.getIntroducingBrokers({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount,
    })

    if (response?.items) {
      setIntroducingBrokers(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await partnershipsApiClient.getIntroducingBrokers({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setIntroducingBrokers(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await partnershipsApiClient.getIntroducingBrokers({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setIntroducingBrokers(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridSelectionChange = (event: GridSelectionChangeEvent) => {
    setIntroducingBrokers(
      introducingBrokers.map((item) => {
        if (item.id === event.dataItem.id) {
          item.selected = !event.dataItem.selected
        }
        return item
      })
    )
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setIntroducingBrokers(
      introducingBrokers.map((item) => {
        return {
          ...item,
          selected: isChecked,
        }
      })
    )
    if (isChecked) {
      setAssignUsersConfirmationModalOpen(true)
    }
  }

  const handleAssignUserAllResultsModalOpen = () => {
    setAssignUsersSearchModalOpen(true)
    setAssignUsersConfirmationModalOpen(false)
  }

  const handleAssignUserFirstPageModalOpen = () => {
    if (getSelectedIds(introducingBrokers).length > 0) {
      setAssignUsersFirstPageModalOpen(true)
      setAssignUsersConfirmationModalOpen(false)
    } else {
      setToast(errorToast('You must select at least one item to assign.'))
    }
  }

  const handleAssignUsersModalClose = () => {
    setAssignUsersFirstPageModalOpen(false)
    setAssignUsersSearchModalOpen(false)
    setAssignUsersConfirmationModalOpen(false)
  }

  const handleAssignUsersConfirmationModalClose = () => {
    setAssignUsersConfirmationModalOpen(false)
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleUserAssignFirstPage = async (event: SelectIBEvent) => {
    const userId = event.value.id
    await partnershipsApiClient.massAssignUserToIbs({
      userId,
      ibIds: getSelectedIds(introducingBrokers),
    })
    setAssignUsersFirstPageModalOpen(false)
    setToast(successToast('Introducing Brokers assigned successfully'))
    await filterFetch()
  }

  const handleUserAssignSearch = async (event: SelectIBEvent) => {
    const userId = event.value.id
    const searchQuery = getQuery(gridState.filter)
    await partnershipsApiClient.massAssignUserToIbs({
      userId,
      criteria: { search: searchQuery },
    })
    setAssignUsersSearchModalOpen(false)
    setToast(successToast('Introducing Brokers assigned successfully'))
    await filterFetch()
  }

  const handleUnAssign = async () => {
    if (getSelectedIds(introducingBrokers).length > 0) {
      await partnershipsApiClient.massUnAssignUsersFromIbs(getSelectedIds(introducingBrokers))
      setAssignUsersFirstPageModalOpen(false)
      setAssignUsersSearchModalOpen(false)
      setToast(successToast('Introducing Brokers unassigned successfully.'))
      await filterFetch()
    } else {
      setToast(errorToast('You must select at least one item to unassign.'))
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isAssignUsersFirstPageModalOpen && (
        <Dialog
          open={isAssignUsersFirstPageModalOpen}
          onOpenChange={(open) => !open && handleAssignUsersModalClose()}
        >
          <SelectIBSAssignTable onSingleSelect={handleUserAssignFirstPage} selectText='Assign' />
        </Dialog>
      )}
      {isAssignUsersSearchModalOpen && (
        <Dialog
          open={isAssignUsersSearchModalOpen}
          onOpenChange={(open) => !open && handleAssignUsersModalClose()}
        >
          <SelectIBSAssignTable onSingleSelect={handleUserAssignSearch} selectText='Assign' />
        </Dialog>
      )}
      {isAssignUsersConfirmationModalOpen && (
        <Dialog
          open={isAssignUsersConfirmationModalOpen}
          onOpenChange={(open) => !open && handleAssignUsersConfirmationModalClose()}
        >
          <DialogContent size='md'>
            Would you like to assign results of all pages or first page results only?
            <DialogFooter>
              <Button onClick={handleAssignUserAllResultsModalOpen}>All Results</Button>
              <Button onClick={handleAssignUserFirstPageModalOpen}>First Page Results</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <Breadcrumbs crumbs={crumbs} />

      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.INTRODUCING_BROKER}
        filterFields={[
          {
            name: IntroducingBrokerFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getIntroducingBrokerFilterFields(),
        ]}
        containerClassName='introducing-broker-table__filter'
        getQuery={getQuery}
        extraHeaderActions={
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button type='button' variant='outline' aria-label='More options' />}
            >
              Actions <ChevronDown />
            </DropdownMenuTrigger>

            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                disabled={!hasPermission(permissions, PermissionsEnum.CanAssignIntroducingBrokers)}
                onSelect={handleAssignUserFirstPageModalOpen}
              >
                Assign
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleUnAssign}
                disabled={!hasPermission(permissions, PermissionsEnum.CanAssignIntroducingBrokers)}
              >
                Unassign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={introducingBrokers}>
          <Grid
            {...gridState}
            resizable
            pageable
            selectedField='selected'
            data={introducingBrokers}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            onSelectionChange={handleGridSelectionChange}
            onHeaderSelectionChange={handleHeaderSelectionChange}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
          >
            <Column
              field='selected'
              width='50px'
              headerSelectionValue={
                introducingBrokers.findIndex((dataItem) => !dataItem.selected) === -1
              }
            />
            {introducingBrokerColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default IntroducingBrokersOverview
